import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {COLUMN_TYPE, SelectedSchemaIds, SpreadsheetColumn} from 'bulk-import/SpreadsheetBulkImportConstants';
import {saveSpreadsheetColumnsMapping} from 'bulk-import/SpreadsheetBulkImportDAO';
import SpreadsheetBulkImportStore from 'bulk-import/SpreadsheetBulkImportStore';
import {getResultSchema, getTagSchema} from 'bulk-import/SpreadsheetBulkImportUtil';
import {UploadFlowType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_LIGHT, GREEN} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent} from 'util/Types';

enum SavingColumnsMappingState {
  MAPPING_PREVIOUSLY_SAVED = 'MAPPING_PREVIOUSLY_SAVED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  SAVING = 'SAVING',
  SAVED = 'SAVED',
  FAILED_WITH_NAME_CONFLICT = 'FAILED_WITH_NAME_CONFLICT',
}

interface IndividualColumnMappings {
  columnName: string;
  columnType: COLUMN_TYPE;
  schemaField: {apiId: string} | null;
}

export interface SpreadsheetColumnsMapping {
  name: string;
  assaySchemaId?: string | null;
  tagSchemaId?: string | null;
  trialSubjectSchemaId?: string | null;
  uploadFlow: UploadFlowType;
  individualColumnMappings: Array<IndividualColumnMappings>;
}

export default function SaveSpreadsheetColumnsMappingForm({
  columns,
  selectedSchemaIds,
  uploadFlow,
  isSaveColumnsMappingFormChecked,
  setIsSaveColumnsMappingFormChecked,
}: {
  columns: Array<SpreadsheetColumn>;
  selectedSchemaIds: SelectedSchemaIds;
  uploadFlow?: string;
  isSaveColumnsMappingFormChecked: boolean;
  setIsSaveColumnsMappingFormChecked: (checked: boolean) => void;
}): JSX.Element | null {
  const [mappingName, setMappingName] = useState<string>('');
  const [saveColumnsMappingError, setSaveColumnsMappingError] = useState<string | null>(null);
  const [savingColumnsMappingState, setSavingColumnsMappingState] = useState<SavingColumnsMappingState>(
    SavingColumnsMappingState.NOT_APPLICABLE
  );

  // If spreadsheetColumnsMappingName is not null, then we've applied a mapping, and not altered any columns
  // since applying the mapping. In this case, there's no use case for saving the mapping again.
  useEffect(() => {
    const spreadsheetColumnsMappingName = SpreadsheetBulkImportStore.getState().spreadsheetColumnsMappingName;
    if (spreadsheetColumnsMappingName != null) {
      setSavingColumnsMappingState(SavingColumnsMappingState.MAPPING_PREVIOUSLY_SAVED);
    }
  }, []);

  if (
    !BenchlingConfig.REGISTRY_ENABLE_SPREADSHEET_COLUMNS_MAPPING ||
    uploadFlow == null ||
    Boolean(selectedSchemaIds.resultSchemaId == null && selectedSchemaIds.tagSchemaId == null)
  ) {
    return null;
  }

  assert(
    Boolean(selectedSchemaIds.resultSchemaId) !== Boolean(selectedSchemaIds.tagSchemaId),
    'Expected either a result schema id or a tag schema id.'
  );
  const schemaName =
    selectedSchemaIds.resultSchemaId != null
      ? getResultSchema(selectedSchemaIds)!.name
      : getTagSchema(selectedSchemaIds)!.name;

  function _triggerSaveSpreadsheetColumnsMappingAnalytics(): void {
    analytics.track('bulk_spreadsheet', 'saved_columns_mapping', {
      nameOfColumnsMapping: mappingName,
      numberOfColumns: columns.length,
      mappedColumns: columns.map((column) => ({
        columnName: column.name,
        columnType: column.type,
      })),
    });
  }

  function handleSaveSpreadsheetColumnsMapping(): void {
    setSavingColumnsMappingState(SavingColumnsMappingState.SAVING);
    const individualColumnMappings = columns.map((column) => ({
      columnName: column.name,
      columnType: column.type,
      schemaField: column.schemaField ? {apiId: column.schemaField.apiId} : null,
    }));
    saveSpreadsheetColumnsMapping({
      name: mappingName,
      assaySchemaId: selectedSchemaIds.resultSchemaId,
      tagSchemaId: selectedSchemaIds.tagSchemaId,
      trialSubjectSchemaId: selectedSchemaIds.trialSubjectSchemaId,
      uploadFlow: uploadFlow as UploadFlowType,
      individualColumnMappings,
    })
      .then(() => {
        _triggerSaveSpreadsheetColumnsMappingAnalytics();
        setSavingColumnsMappingState(SavingColumnsMappingState.SAVED);
        setIsSaveColumnsMappingFormChecked(false);
      })
      .catch((error) => {
        const errorMessage = notifications.getFriendlyError(error);
        if (errorMessage != null && errorMessage.includes('Name already exists')) {
          setSaveColumnsMappingError(errorMessage);
          setSavingColumnsMappingState(SavingColumnsMappingState.FAILED_WITH_NAME_CONFLICT);
        } else {
          setSavingColumnsMappingState(SavingColumnsMappingState.NOT_APPLICABLE);
          throw error;
        }
      });
  }

  const saveButton = (
    <Button
      text="Save"
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.SMALL}
      onClick={handleSaveSpreadsheetColumnsMapping}
      dataTestAttrs={{component: 'SaveSpreadsheetColumnsMappingForm', element: 'button'}}
    />
  );
  switch (savingColumnsMappingState) {
    case SavingColumnsMappingState.MAPPING_PREVIOUSLY_SAVED:
      return null;
    case SavingColumnsMappingState.NOT_APPLICABLE:
    case SavingColumnsMappingState.FAILED_WITH_NAME_CONFLICT:
      return (
        <div className={css(styles.SaveColumnsMappingBox)}>
          <div className="u-flex-display">
            <Tooltip text="Choose this to save the column mappings for use when importing similar spreadsheets in the future">
              <CheckboxInput
                id="save-columns-mapping"
                label=""
                checked={isSaveColumnsMappingFormChecked}
                onChange={(e: InputChangeEvent) => {
                  setIsSaveColumnsMappingFormChecked(e.target.checked);
                }}
                dataTestAttrs={{component: 'SaveSpreadsheetColumnsMappingForm', element: 'checkbox'}}
              />
            </Tooltip>
            <FieldLabel className="checkboxInput-label" label={`Save column mappings for "${schemaName}"`} />
          </div>
          {isSaveColumnsMappingFormChecked && (
            <div className="u-small-padding-top">
              <TextField
                label="Saved mapping name"
                value={mappingName}
                onChange={(e: InputChangeEvent) => {
                  setMappingName(e.target.value);
                }}
                button={saveButton}
                error={{message: saveColumnsMappingError, style: InputErrorMessageStyle.ERROR}}
                dataTestAttrs={{component: 'SaveSpreadsheetColumnsMappingForm', element: 'input'}}
              />
            </div>
          )}
        </div>
      );
    case SavingColumnsMappingState.SAVING:
      return (
        <div className={classNames(css(styles.SaveColumnsMappingBox), 'u-flex-display')}>
          <BenchlingIcon
            icon="spinner-third"
            color={GRAY_DARK}
            className={classNames(css(styles.IconPadding))}
          />
          <div>Saving column mappings.</div>
        </div>
      );
    case SavingColumnsMappingState.SAVED:
      return (
        <div className={classNames(css(styles.SaveColumnsMappingBox), 'u-flex-display')}>
          <BenchlingIcon
            icon="status-success"
            color={GREEN}
            className={classNames(css(styles.IconPadding))}
          />
          <div>Column mappings saved.</div>
        </div>
      );
    default:
      return null;
  }
}

const styles = StyleSheet.create({
  SaveColumnsMappingBox: {
    borderRadius: '5px',
    padding: '16px',
    border: `1px solid ${GRAY_LIGHT}`,
  },
  IconPadding: {
    padding: '3px 10px 0 0',
  },
});
